import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Reducers } from '@app/reducers';
import { AppService } from '@app/services/app.service';
import { LoginService } from '@login/services/login.service';
import { TranslateService } from '@ngx-translate/core';
import { LoginRequestDto } from '@services/authentication/model/loginRequestDto';
import { ToastrService } from 'ngx-toastr';
import { catchError, filter, first, map, switchMap, take } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { ReloginRequestDto } from '@services/authentication/model/reloginRequestDto';
import { SharedService } from '@shared/shared.service';
import dayjs from "dayjs";
import * as jwt_decode from 'jwt-decode';
import * as loginActions from '@login/ngrx/login.actions';
import * as selectFromRouter from '@app/ngrx/app.selectors';

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private currentUrlId$: Observable<number>;
  private currentUrlId: number;

  constructor(
    public loginService: LoginService,
    private store$: Store<Reducers>,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService,
    private toastrService: ToastrService,
    private appService: AppService,
  ) {
    this.currentUrlId$ = this.store$.pipe(select(selectFromRouter.getCurrentRouteId));
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.currentUrlId$.pipe(
      take(10),
    ).subscribe((routerId: number) => {
      this.currentUrlId = routerId;
    });
    return next.handle(request).pipe(
      catchError((error: any) => {
        if (error.status !== 401) {
          return throwError(error);
        }

        if (this.refreshTokenInProgress) {
          return this.refreshTokenSubject.pipe(
            filter(result => result !== null),
            take(1),
            switchMap(() => next.handle(this.addAuthenticationToken(request)))
          );
        } else {
          this.refreshTokenInProgress = true;

          // Set the refreshTokenSubject to null so that subsequent API calls will wait until the new token has been retrieved
          this.refreshTokenSubject.next(null);

          // Call auth.refreshAccessToken(this is an Observable that will be returned)
          const refreshToken: string | null = localStorage ? localStorage.getItem('refresh_token') : null;
          const deviceId = this.appService.deviceAndBrowserUuid.uniqueUuid;
          const reloginCreds: ReloginRequestDto = {
            clientId: LoginRequestDto.ClientIdEnum.BackOffice,
            clientType: ReloginRequestDto.ClientTypeEnum.Web,
            ...deviceId && {deviceId},
            ...refreshToken && {refreshToken}
          };

          return this.loginService.reLogin(reloginCreds).pipe(
            switchMap((token: any) => {
              const tokenExpires = (dayjs().unix() + token.expiresIn).toString();

              localStorage.removeItem('token');
              localStorage.removeItem('refresh_token');
              localStorage.removeItem('tokenExpiresOn');
              localStorage.setItem('token', token.accessToken);
              localStorage.setItem('refresh_token', token.refreshToken);
              localStorage.setItem('tokenExpiresOn', tokenExpires);
              localStorage.setItem('appVersion', token.version);

              this.refreshTokenInProgress = false;
              this.refreshTokenSubject.next(token);

              /** Set Privileges if any */
              if (token.accessToken) {
                const tokenPermissions: any = jwt_decode.default(token.accessToken);

                /** Get HEX strings */
                const hexStrings: string[] = [];
                let hexStringWithAssociations: {association: string, hex: string}[] = [];

                if (tokenPermissions && tokenPermissions.permissions) {
                  // @ts-ignore
                  Object.entries(tokenPermissions.permissions).forEach((
                    [hexString, associationsArray]: [string, {t: string, bp?: string[]}[]]
                  ) => {
                    hexStrings.push(hexString);
                    /** Get Associations IDs */
                    const itemAssociationsWithHex = associationsArray && associationsArray.length ?
                      associationsArray.map((item: {t: string, bp?: string[]}) => {
                        return {association: item.t, hex: hexString, ...item.bp && {bp: item.bp}};
                      }) : null;

                    if (itemAssociationsWithHex) {
                      hexStringWithAssociations = [...hexStringWithAssociations, ...itemAssociationsWithHex];
                    }
                  });
                }

                /** Get Permissions by HEX string */
                this.loginService.getPermissionsByHex(hexStrings).pipe(
                  filter(data => !!data),
                  first(),
                  map((result: any) => {
                    this.store$.dispatch(
                      new loginActions.SetPermissions(SharedService.transformDataToPermissions(hexStringWithAssociations, result))
                    );
                  }),
                  catchError((error: HttpErrorResponse) => {
                    SharedService.showErrorWithTitle(
                      this.translateService,
                      this.toastrService,
                      `responseErrors.${error.error.message}`
                    );
                    return of();
                  })
                ).subscribe();
              }

              return next.handle(this.addAuthenticationToken(request));
            }),
            catchError(() => {
              this.refreshTokenInProgress = false;
              this.store$.dispatch(new loginActions.LogOut());
              setTimeout(() => {
                this.router.navigate(['/', 'login'], {relativeTo: this.activatedRoute});
              }, 100);

              const clonedError = error ? JSON.parse(JSON.stringify(error)) : null;
              if (clonedError) {
                clonedError.error.message = this.currentUrlId && this.currentUrlId < 3 ? 'hidden' : 'invalid_refresh_token';
              }

              /** Show Error if Refresh Token's expired and navigating from Select Cabinet Page (Root) */
              if (
                clonedError &&
                clonedError.error &&
                clonedError.error.message === 'invalid_refresh_token' &&
                this.router &&
                this.router.url === '/' &&
                this.currentUrlId &&
                this.currentUrlId > 2
              ) {
                SharedService.showErrorWithTitle(this.translateService, this.toastrService, clonedError.error.message);
              }
              return throwError(clonedError);
            })
          );
        }
      })
    );
  }

  // @ts-ignore
  addAuthenticationToken(request) {
    // Get access token from Local Storage
    const accessToken = localStorage ? localStorage.getItem('token') : null;

    // If access token is null this means that user is not logged in
    // And we return the original request
    if (!accessToken) {
      return request;
    }

    // We clone the request, because the original request is immutable
    return request.clone({
      setHeaders: {
        Authorization: 'Bearer ' + accessToken
      }
    });
  }

}
